import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PlusCircle, Edit, Trash2, RotateCcw, CreditCard, Check, Users, Tag } from 'lucide-react';
import { toast } from 'sonner';

const STAKEHOLDER_TYPES = [
  'Logistics Provider', 'Shipper', 'Retailer', 'Manufacturer',
  'Academic', 'Consultant', 'Technology Provider', 'Government',
  'NGO', 'Investor', 'Media', 'Other',
];

export function AdminSubscriptionsTab() {
  const [subPlans, setSubPlans] = useState([]);
  const [subOverrides, setSubOverrides] = useState([]);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '', description: '', amount: '', currency: 'gbp',
    duration_days: 30, features: '', stakeholder_types: [],
    is_active: true, sort_order: 0,
  });
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ user_id: '', plan_id: '', custom_amount: '', free_access: false, reason: '', expires_at: '' });
  const [overrideUserSearch, setOverrideUserSearch] = useState('');
  const [overrideUserResults, setOverrideUserResults] = useState([]);

  useEffect(() => { loadSubPlans(); loadSubOverrides(); }, []);

  const loadSubPlans = async () => {
    try {
      const res = await fetch('/api/admin/subscription-plans', { credentials: 'include' });
      if (res.ok) setSubPlans(await res.json());
    } catch {}
  };

  const loadSubOverrides = async () => {
    try {
      const res = await fetch('/api/admin/subscription-overrides', { credentials: 'include' });
      if (res.ok) setSubOverrides(await res.json());
    } catch {}
  };

  const handleSavePlan = async () => {
    if (!planForm.name.trim()) { toast.error('Plan name required'); return; }
    try {
      const payload = {
        ...planForm,
        amount: parseFloat(planForm.amount) || 0,
        duration_days: parseInt(planForm.duration_days) || 30,
        features: planForm.features.split('\n').filter(f => f.trim()),
        stakeholder_types: planForm.stakeholder_types,
        sort_order: parseInt(planForm.sort_order) || 0,
      };
      const method = editingPlan ? 'PUT' : 'POST';
      const url = editingPlan
        ? `/api/admin/subscription-plans/${editingPlan.plan_id}`
        : '/api/admin/subscription-plans';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success(editingPlan ? 'Plan updated' : 'Plan created');
      setShowPlanForm(false); setEditingPlan(null);
      resetPlanForm();
      loadSubPlans();
    } catch (e) { toast.error(e.message); }
  };

  const handleDeletePlan = async (planId) => {
    if (!(await window.appConfirm('Delete this plan? Users on this plan will NOT be affected.'))) return;
    try {
      await fetch(`/api/admin/subscription-plans/${planId}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Plan deleted'); loadSubPlans();
    } catch { toast.error('Failed'); }
  };

  const resetPlanForm = () => {
    setPlanForm({ name: '', description: '', amount: '', currency: 'gbp', duration_days: 30, features: '', stakeholder_types: [], is_active: true, sort_order: 0 });
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name,
      description: plan.description || '',
      amount: String(plan.amount),
      currency: plan.currency || 'gbp',
      duration_days: plan.duration_days || 30,
      features: (plan.features || []).join('\n'),
      stakeholder_types: plan.stakeholder_types || [],
      is_active: plan.is_active !== false,
      sort_order: plan.sort_order || 0,
    });
    setShowPlanForm(true);
  };

  const toggleStakeholder = (type) => {
    setPlanForm(prev => ({
      ...prev,
      stakeholder_types: prev.stakeholder_types.includes(type)
        ? prev.stakeholder_types.filter(t => t !== type)
        : [...prev.stakeholder_types, type],
    }));
  };

  const searchUsersForOverride = async (q) => {
    setOverrideUserSearch(q);
    if (q.length < 2) { setOverrideUserResults([]); return; }
    try {
      const res = await fetch(`/api/members?search=${encodeURIComponent(q)}&limit=5`, { credentials: 'include' });
      if (res.ok) { const data = await res.json(); setOverrideUserResults(data.users || []); }
    } catch {}
  };

  const handleSaveOverride = async () => {
    if (!overrideForm.user_id) { toast.error('Select a user'); return; }
    try {
      const res = await fetch('/api/admin/subscription-overrides', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(overrideForm),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success('Override created');
      setShowOverrideForm(false);
      setOverrideForm({ user_id: '', plan_id: '', custom_amount: '', free_access: false, reason: '', expires_at: '' });
      setOverrideUserSearch(''); setOverrideUserResults([]);
      loadSubOverrides();
    } catch (e) { toast.error(e.message); }
  };

  const handleDeleteOverride = async (overrideId) => {
    if (!(await window.appConfirm('Remove this override?'))) return;
    try {
      await fetch(`/api/admin/subscription-overrides/${overrideId}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Override removed'); loadSubOverrides();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-subscriptions-content">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-bold font-heading">Subscription Management</h2>
        <Button size="sm" variant="outline" onClick={() => { loadSubPlans(); loadSubOverrides(); }} data-testid="refresh-subs-btn">
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Plans Section */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800">Subscription Plans</h3>
          <Button size="sm" onClick={() => { setEditingPlan(null); resetPlanForm(); setShowPlanForm(true); }}
            className="bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="create-plan-btn">
            <PlusCircle className="h-3.5 w-3.5 mr-1.5" /> New Plan
          </Button>
        </div>
        {subPlans.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No plans yet. Click "New Plan" to create one.</p>
        ) : (
          <div className="space-y-3">
            {subPlans.map(plan => (
              <Card key={plan.plan_id} className="p-4 border border-slate-200" data-testid={`plan-${plan.plan_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="h-4 w-4 text-brand-navy flex-shrink-0" />
                      <span className="font-semibold text-sm">{plan.name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${plan.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {plan.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                      <span className="text-sm font-bold text-brand-navy">
                        {plan.currency?.toUpperCase()} {plan.amount?.toFixed(2)}
                      </span>
                      <span className="text-xs text-slate-400">/ {plan.duration_days} days</span>
                    </div>
                    {plan.description && (
                      <p className="text-xs text-slate-500 mb-2">{plan.description}</p>
                    )}

                    {/* Features */}
                    {plan.features?.length > 0 && (
                      <div className="mb-2">
                        <p className="text-[10px] uppercase text-slate-400 font-semibold mb-1">Features</p>
                        <ul className="space-y-0.5">
                          {plan.features.map((f, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                              <Check className="h-3 w-3 text-brand-sage mt-0.5 flex-shrink-0" /> {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Stakeholder Types */}
                    {plan.stakeholder_types?.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Users className="h-3 w-3 text-slate-400" />
                        {plan.stakeholder_types.map(st => (
                          <span key={st} className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-brand-navy/10 text-brand-navy">{st}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openEditPlan(plan)} data-testid={`edit-plan-${plan.plan_id}`}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeletePlan(plan.plan_id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Free Tier Info */}
      <Card className="p-4 bg-slate-50 border-slate-200">
        <p className="text-xs text-slate-500 font-medium uppercase mb-1">Free Tier (always included)</p>
        <ul className="grid grid-cols-2 gap-1">
          {['Member directory access', 'Event listings & RSVP', 'Community news feed', 'Direct messaging', 'Smart networking matches', 'Sponsor directory'].map(f => (
            <li key={f} className="flex items-center gap-1.5 text-xs text-slate-600">
              <Check className="h-3 w-3 text-slate-400" /> {f}
            </li>
          ))}
        </ul>
      </Card>

      {/* Overrides Section */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800">User Price Overrides</h3>
          <Button size="sm" onClick={() => setShowOverrideForm(true)} className="bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="create-override-btn">
            <PlusCircle className="h-3.5 w-3.5 mr-1.5" /> New Override
          </Button>
        </div>
        {subOverrides.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No user overrides.</p>
        ) : (
          <div className="space-y-2">
            {subOverrides.map(ov => (
              <div key={ov.override_id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100" data-testid={`override-${ov.override_id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{ov.user_name || ov.user_email}</span>
                    {ov.free_access && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">FREE ACCESS</span>}
                    {ov.custom_amount !== null && !ov.free_access && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">CUSTOM: {ov.custom_amount?.toFixed?.(2) || ov.custom_amount}</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {ov.reason && `Reason: ${ov.reason} · `}
                    {ov.expires_at ? `Expires: ${new Date(ov.expires_at).toLocaleDateString()}` : 'No expiry'}
                  </p>
                </div>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteOverride(ov.override_id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Plan Form Dialog */}
      <Dialog open={showPlanForm} onOpenChange={v => { setShowPlanForm(v); if (!v) setEditingPlan(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="plan-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingPlan ? 'Edit Plan' : 'Create Subscription Plan'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Plan Name *</Label><Input value={planForm.name} onChange={e => setPlanForm({...planForm, name: e.target.value})} data-testid="plan-name-input" /></div>
              <div><Label>Amount ({planForm.currency?.toUpperCase()}) *</Label><Input type="number" step="0.01" value={planForm.amount} onChange={e => setPlanForm({...planForm, amount: e.target.value})} data-testid="plan-amount-input" /></div>
            </div>
            <div><Label>Description</Label><Input value={planForm.description} onChange={e => setPlanForm({...planForm, description: e.target.value})} placeholder="Short description shown to users" /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Currency</Label>
                <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" value={planForm.currency} onChange={e => setPlanForm({...planForm, currency: e.target.value})}>
                  <option value="gbp">GBP</option><option value="usd">USD</option><option value="eur">EUR</option>
                </select>
              </div>
              <div><Label>Duration (days)</Label><Input type="number" value={planForm.duration_days} onChange={e => setPlanForm({...planForm, duration_days: e.target.value})} /></div>
              <div><Label>Sort Order</Label><Input type="number" value={planForm.sort_order} onChange={e => setPlanForm({...planForm, sort_order: e.target.value})} /></div>
            </div>
            <div>
              <Label>Features / Bullet Points (one per line)</Label>
              <Textarea
                className="min-h-[100px] text-sm"
                value={planForm.features}
                onChange={e => setPlanForm({...planForm, features: e.target.value})}
                placeholder={"Full course library access\nSLL keynote presentations\nPriority event registration"}
                data-testid="plan-features-input"
              />
              <p className="text-xs text-slate-400 mt-1">These bullet points are displayed to users on the pricing page</p>
            </div>
            <div>
              <Label>Stakeholder Types (link plan to user categories)</Label>
              <div className="flex flex-wrap gap-2 mt-1.5 p-3 border border-slate-200 rounded-md" data-testid="stakeholder-types-selector">
                {STAKEHOLDER_TYPES.map(type => {
                  const isSelected = planForm.stakeholder_types.includes(type);
                  return (
                    <button key={type} type="button"
                      data-testid={`stakeholder-tag-${type.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => toggleStakeholder(type)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        isSelected ? 'bg-brand-navy text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
              {planForm.stakeholder_types.length > 0 && (
                <p className="text-xs text-slate-400 mt-1">{planForm.stakeholder_types.length} stakeholder types linked</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="plan-active" checked={planForm.is_active} onChange={e => setPlanForm({...planForm, is_active: e.target.checked})} />
              <Label htmlFor="plan-active" className="cursor-pointer">Active</Label>
            </div>
            <Button onClick={handleSavePlan} className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="save-plan-btn">
              {editingPlan ? 'Update Plan' : 'Create Plan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Override Form Dialog */}
      <Dialog open={showOverrideForm} onOpenChange={setShowOverrideForm}>
        <DialogContent className="max-w-lg" data-testid="override-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">Create User Override</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Search User *</Label>
              <Input value={overrideUserSearch} onChange={e => searchUsersForOverride(e.target.value)} placeholder="Search by name or email..." data-testid="override-user-search" />
              {overrideUserResults.length > 0 && (
                <div className="border rounded-md mt-1 max-h-32 overflow-y-auto">
                  {overrideUserResults.map(u => (
                    <button key={u.user_id} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                      onClick={() => { setOverrideForm({...overrideForm, user_id: u.user_id}); setOverrideUserSearch(u.name || u.email); setOverrideUserResults([]); }}>
                      <span className="font-medium">{u.name}</span>
                      <span className="text-slate-400 text-xs">{u.email}</span>
                    </button>
                  ))}
                </div>
              )}
              {overrideForm.user_id && <p className="text-xs text-green-600 mt-1">User selected: {overrideForm.user_id}</p>}
            </div>
            <div>
              <Label>Plan</Label>
              <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" value={overrideForm.plan_id}
                onChange={e => setOverrideForm({...overrideForm, plan_id: e.target.value})} data-testid="override-plan-select">
                <option value="">Select plan...</option>
                {subPlans.filter(p => p.is_active).map(p => (
                  <option key={p.plan_id} value={p.plan_id}>{p.name} ({p.currency?.toUpperCase()} {p.amount?.toFixed(2)})</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="free-access" checked={overrideForm.free_access} onChange={e => setOverrideForm({...overrideForm, free_access: e.target.checked})} />
              <Label htmlFor="free-access" className="cursor-pointer">Grant Free Access</Label>
            </div>
            {!overrideForm.free_access && (
              <div><Label>Custom Amount (optional)</Label><Input type="number" step="0.01" value={overrideForm.custom_amount} onChange={e => setOverrideForm({...overrideForm, custom_amount: e.target.value})} /></div>
            )}
            <div><Label>Expires At (optional)</Label><Input type="date" value={overrideForm.expires_at} onChange={e => setOverrideForm({...overrideForm, expires_at: e.target.value})} /></div>
            <div><Label>Reason</Label><Input value={overrideForm.reason} onChange={e => setOverrideForm({...overrideForm, reason: e.target.value})} placeholder="Why this override?" /></div>
            <Button onClick={handleSaveOverride} className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="save-override-btn">Create Override</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
